import React, { useRef, useState } from 'react';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeselectIcon from '@mui/icons-material/Deselect';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import { useTranslation } from 'react-i18next';
import { DndColumn } from '../types';
import { SelectionType } from '../hooks/useSelectionState';
import { filterDndItems } from '../const';
import { useSelectionContext } from '../contexts/SelectionContext';
import { TableHeaderTooltip } from '@applications-terrains/birdz-react-library';
import useDebouncedValue from '../../../../hooks/useDebouncedValue';
import { Droppable, VisibleDndCard } from './DndPrimitives';

export const DndColumnBox = ({
  column,
  columns,
  isOpen,
  toggle,
  isDropDisabled,
  emptyMessage
}: {
  column: DndColumn;
  columns: DndColumn[];
  isOpen: (columnId: string, itemCode: string) => boolean;
  toggle: (columnId: string, itemCode: string) => void;
  isDropDisabled?: boolean;
  /** Message affiché quand la colonne est vide (optionnel). */
  emptyMessage?: string;
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const debouncedValue = useDebouncedValue(inputValue, 300);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const { isSelected, removeMany, addMany, isReadOnly } = useSelectionContext();

  const itemsToShow = filterDndItems(column.items, debouncedValue);

  const allSelected =
    itemsToShow.length > 0 && itemsToShow.every((item) => isSelected(column.id, 'parent', item.id));

  const handleSelectAll = () => {
    const parentItems = itemsToShow.map((item) => ({
      columnId: column.id,
      type: 'parent' as SelectionType,
      itemId: item.id
    }));
    if (allSelected) {
      removeMany(parentItems);
    } else {
      const childItems = itemsToShow.flatMap((item) =>
        item.children.map((child) => ({
          columnId: column.id,
          type: 'child' as SelectionType,
          itemId: child.id
        }))
      );
      removeMany(childItems);
      addMany(parentItems);
    }
  };

  return (
    <Box
      sx={{
        width: `calc((100% - ${(columns.length - 1) * 16}px) / ${columns.length})`,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0
      }}
    >
      <Droppable id={column.id} columns={columns} isDropDisabled={isDropDisabled}>
        <Box display="flex" alignItems="center">
          <Box sx={{ position: 'relative', width: '100%' }}>
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                position: 'absolute',
                left: 0,
                top: 4,
                fontWeight: 'bold'
              }}
            >
              {column.title}
            </Typography>
            <TextField
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                const selectedItems = itemsToShow.flatMap((item) => [
                  { columnId: column.id, type: 'parent' as SelectionType, itemId: item.id },
                  ...item.children.map((child) => ({
                    columnId: column.id,
                    type: 'child' as SelectionType,
                    itemId: child.id
                  }))
                ]);
                removeMany(selectedItems);
              }}
              size="small"
              variant="standard"
              fullWidth
              disabled={column.items.length === 0}
              InputProps={{
                startAdornment: <Box sx={{ width: '100%' }} />,
                endAdornment: (
                  <Box display="flex" alignItems="center" justifyContent="center">
                    <SearchIcon
                      sx={{
                        mx: 1,
                        color: column.items.length === 0 ? 'disabled' : 'action.active'
                      }}
                    />
                    {!isReadOnly && (
                      <Box sx={{ mr: -1, display: 'flex', transition: 'margin 0.3s ease' }}>
                        <Box
                          sx={{
                            mb: '2px',
                            width: 34,
                            transition: 'width 0.3s ease',
                            overflow: 'hidden'
                          }}
                        >
                          <TableHeaderTooltip
                            title={
                              <Box sx={{ py: 1, fontSize: '0.7rem', color: '#666666' }}>
                                {allSelected ? t('deselectAll') : t('selectAll')}
                              </Box>
                            }
                            element={
                              <IconButton
                                size="small"
                                sx={{ height: 34 }}
                                onClick={handleSelectAll}
                              >
                                {allSelected ? <DeselectIcon /> : <SelectAllIcon />}
                              </IconButton>
                            }
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                ),
                inputProps: { style: { textAlign: 'right' } }
              }}
            />
          </Box>
        </Box>

        <Box
          ref={scrollContainerRef}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            mt: 2,
            gap: 2,
            zIndex: 0
          }}
        >
          {emptyMessage && column.items.length === 0 && !inputValue ? (
            <Box fontWeight="300" color="grey.800">
              {emptyMessage}
            </Box>
          ) : itemsToShow.length === 0 && inputValue ? (
            <Box fontWeight="300" color="grey.800">
              {t('noResultsFound')}
            </Box>
          ) : (
            itemsToShow
              .filter((item) => item.id)
              .map((item) => (
                <VisibleDndCard
                  key={item.id}
                  item={item}
                  columnId={column.id}
                  isOpen={isOpen}
                  onToggleAccordion={(code) => toggle(column.id, code)}
                  scrollContainerRef={scrollContainerRef}
                />
              ))
          )}
        </Box>
      </Droppable>
    </Box>
  );
};

export default DndColumnBox;
